import { mediaType } from '../../firms/firm/mapper'

export function extractVideoIdFromUrl(url: string | null) {
  if (!url || typeof url !== 'string') {
    return null
  }
  const str = url
  let match = str.match(/[?&]v=([^?&]+)/)

  if (!match) {
    match = str.match(/youtu\.be\/([^?&]+)/)
  }

  if (!match) {
    match = str.match(/embed\/([^?&]+)/)
  }

  if (!match) {
    match = str.match(/shorts\/([^?&]+)/)
  }

  return match ? match[1] : null
}

export async function fetchYouTubeVideoThumbnail(
  media: string | null,
  youTubeApiKey: string | null,
): Promise<string | null> {
  const apiKey = youTubeApiKey || process.env.YOUTUBE_API_KEY
  if (!media || !apiKey) {
    return null
  }

  const mediaId = extractVideoIdFromUrl(media)

  if (!mediaId) {
    return null
  }

  const response = await fetch(
    `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${mediaId}&key=${youTubeApiKey}`,
  )

  if (!response.ok) {
    throw new Error(
      `Error fetching YouTube video thumbnail: ${response.statusText}`,
    )
  }
  const data = await response.json()
  const videoData = data.items[0]
  return (
    videoData?.snippet?.thumbnails?.standard?.url ??
    videoData?.snippet?.thumbnails?.high?.url ??
    videoData?.snippet?.thumbnails?.medium?.url ??
    videoData?.snippet?.thumbnails?.default?.url
  )
}

/**
 * Format a YouTube video URL to be used in an iframe.
 * Some times the URL is in the form of `https://www.youtube.com/watch?v=...` and we need to convert it to `https://www.youtube.com/embed/...`
 */
export function formatMediaUrlForIframe(url: string | null) {
  if (!url) {
    return null
  }
  if (url.includes('watch?v=')) {
    return url.replace('watch?v=', 'embed/')
  }

  if (url.includes('shorts/')) {
    return url.replace('shorts/', 'embed/')
  }

  return url
}

export function isShortsVideo(url: string | null) {
  if (!url) {
    return false
  }
  return url.includes('shorts/')
}

/**
 * Check if a media is a portrait picture.
 * @param media The media to check.
 * @returns True if the media is a portrait picture, false otherwise.
 */
export function isPortaitPicture<T extends { height: number; width: number }>(
  media: T | null,
) {
  if (!media || typeof media === 'string') {
    return false
  }
  return media.width < media?.height
}

export function formatMediaFromGraphqlRequest<
  T extends mediaType & { url?: string },
>(media: T | null): mediaType | null {
  if (!media) {
    return null
  }

  if (media.url) {
    return {
      ...media,
      id: media.id,
      media: media.url,
      video: true,
    }
  }
  return {
    ...media,
    id: media.id,
    media: media.local?.localFile?.childImageSharp?.gatsbyImageData || null,
    video: false,
  }
}
